import { createSlice, createSelector } from '@reduxjs/toolkit';
import { ensureArray } from '../common/utils/arrayUtilities';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';

const compareNote = (n1, n2) => n1.id === n2.id;

const taskListNoteSlice = createSlice({
    name: 'taskListNote',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            const data = ensureArray(action.payload);
            data.forEach(dt => {
                delete state.data[dt.listid?.toString()];
            });
            data.forEach(dt => {
                const listid = dt.listid?.toString();
                if (!state.data[listid]) {
                    state.data[listid] = []
                }
                state.data[listid].push(dt);
            });
        },
        addOrUpdate: (state, action) => {
            for (const note of ensureArray(action.payload)) {
                const listid = note.listid?.toString();
                if (state.data[listid]) {
                    let idx = state.data[listid].findIndex(a => compareNote(a, note));
                    if (idx >= 0) {
                        state.data[listid].splice(idx, 1, note);
                    } else {
                        state.data[listid].push(note);
                    }
                } else {
                    state.data[listid] = [note];
                }
            }
        },
        remove: (state, action) => {
            for (const note of ensureArray(action.payload)) {
                const listid = note.listid?.toString();
                if (state.data[listid]) {
                    let idx = state.data[listid].findIndex(a => compareNote(a, note));
                    if (idx >= 0) {
                        state.data[listid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => taskListNoteSlice.getInitialState());
    }
});

// #region network operations
export const fetchTaskListNote = listid => network => network.get('/api/v1/taskList/' + encodeURIComponent(listid) + '/notes', taskListNoteSlice.actions.set);
export const createNote = (network, note) => network.post('/api/v1/taskListNote', { ...note, autogenerated: note.autogenerated === true || note.autogenerated === '1' || note.autogenerated === 1 ? 1 : 0 }, taskListNoteSlice.actions.addOrUpdate);
export const strikeNote = (network, note) => network.put('/api/v1/taskListNote', note, taskListNoteSlice.actions.addOrUpdate);
// #endregion

// #region selectors
const selectNote = state => state.taskListNote.data;

// we now make a reselect to memoize a select function parameterized by listid and expose that factory through a convenience function
const selectNoteByTaskListID = createSelector([selectNote, (note, listid) => listid], (note, listid) => {
    return note[listid?.toString()] ?? []
});
const selectTaskListNote = listid => state => selectNoteByTaskListID(state, listid);
// #endregion

// #region listeners
const listen = {
    taskListNoteCreated: (note, { dispatch }) => dispatch(taskListNoteSlice.actions.addOrUpdate(note)),
    taskListNoteStruck: (note, { dispatch }) => dispatch(taskListNoteSlice.actions.addOrUpdate(note))
}
// #endregion
const options = { cleanup: { root: 'taskListNote', all: state => Object.values(state.taskListNote.data).flat(), removeAll: taskListNoteSlice.actions.remove, remove: taskListNoteSlice.actions.remove } };
export const [useTaskListNotes] = dataHookFactory(selectTaskListNote, initMapFactory(fetchTaskListNote, listen), options);

const reducerDetails = { name: taskListNoteSlice.name, reducer: taskListNoteSlice.reducer };
export default reducerDetails;